import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Helmet } from 'react-helmet';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const PokerEVCalculator = () => {
  const [pokerData, setPokerData] = useState({
    potSize: '',
    betSize: '',
    winProbability: ''
  });

  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setPokerData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const calculateEV = () => {
    const { potSize, betSize, winProbability } = pokerData;

    const potSizeNum = parseFloat(potSize) || 0;
    const betSizeNum = parseFloat(betSize) || 0;
    const winProbabilityNum = parseFloat(winProbability) || 0;

    if (potSizeNum === 0 || betSizeNum === 0 || winProbabilityNum === 0) {
      setError('Please enter valid pot size, bet size, and win probability');
      return;
    }

    if (winProbabilityNum < 0 || winProbabilityNum > 100) {
      setError('Win probability must be between 0 and 100');
      return;
    }

    const totalPot = potSizeNum + betSizeNum;
    const ev = (winProbabilityNum / 100) * totalPot - betSizeNum;

    setResults({
      ev
    });

    // Generate chart data for a range of win probabilities
    const winProbabilities = Array.from({ length: 11 }, (_, i) => i * 10);
    const evValues = winProbabilities.map(prob => (prob / 100) * totalPot - betSizeNum);

    setChartData({
      labels: winProbabilities.map(prob => `${prob}%`),
      datasets: [
        {
          label: 'Expected Value',
          data: evValues,
          backgroundColor: 'rgba(75, 192, 192, 0.5)',
          borderColor: 'rgba(75, 192, 192, 1)',
          borderWidth: 1
        }
      ]
    });
  };

  const exampleData = {
    potSize: 100,
    betSize: 50,
    winProbability: 60,
    ev: 10
  };

  return (
    <div className="min-h-screen bg-gray-100 py-8">
      <Helmet>
        <title>Poker EV Calculator | Calculate Expected Value</title>
        <meta name="description" content="Calculate the expected value of your poker bet with our Poker EV Calculator. Enter pot size, bet size, and win probability to get the EV." />
        <meta name="keywords" content="Poker EV calculator, expected value, poker strategy, pot size, bet size, win probability" />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
          <div className="flex items-center mb-8">
            <div className="text-4xl text-blue-600 mr-4">EV</div>
            <h1 className="text-3xl font-bold text-gray-800">Poker EV Calculator</h1>
          </div>

          <div className="text-gray-600 mb-8">
            <p>The Poker EV Calculator helps you calculate the expected value of your bet based on the pot size, your bet size, and your win probability.</p>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-white rounded-lg shadow-lg p-6"
          >
            <div className="grid grid-cols-1 gap-6 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Pot Size (in currency):</label>
                <input
                  type="number"
                  id="potSize"
                  name="potSize"
                  value={pokerData.potSize}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter pot size"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Bet Size (in currency):</label>
                <input
                  type="number"
                  id="betSize"
                  name="betSize"
                  value={pokerData.betSize}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter bet size"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Win Probability (in %):</label>
                <input
                  type="number"
                  id="winProbability"
                  name="winProbability"
                  value={pokerData.winProbability}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter win probability"
                />
              </div>
            </div>

            <button
              onClick={calculateEV}
              className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors duration-200"
            >
              Calculate EV
            </button>

            {error && (
              <div className="mt-4 p-4 text-red-700 bg-red-100 rounded-lg">
                {error}
              </div>
            )}

            {results && (
              <div className="mt-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-6">EV Analysis</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h3 className="text-lg font-semibold text-gray-700 mb-2">Pot Size</h3>
                    <p className="text-gray-600">{pokerData.potSize} currency</p>
                  </div>

                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h3 className="text-lg font-semibold text-gray-700 mb-2">Bet Size</h3>
                    <p className="text-gray-600">{pokerData.betSize} currency</p>
                  </div>

                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h3 className="text-lg font-semibold text-gray-700 mb-2">Win Probability</h3>
                    <p className="text-gray-600">{pokerData.winProbability}%</p>
                  </div>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Calculation Results</h3>
                  <p className="text-gray-600">Expected Value: {results.ev.toFixed(2)} currency</p>
                </div>

                {chartData && (
                  <div className="bg-white p-6 rounded-lg shadow-md mb-8">
                    <Bar
                      data={chartData}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        scales: {
                          y: {
                            beginAtZero: true,
                            title: {
                              display: true,
                              text: 'Expected Value (currency)'
                            }
                          },
                          x: {
                            title: {
                              display: true,
                              text: 'Win Probability (%)'
                            }
                          }
                        },
                        plugins: {
                          title: {
                            display: true,
                            text: 'Expected Value for Different Win Probabilities',
                            font: {
                              size: 16
                            }
                          }
                        }
                      }}
                    />
                  </div>
                )}
              </div>
            )}
          </motion.div>

          <div className="mt-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Example Calculation <span className="text-blue-600">📝</span></h2>
            <div className="bg-white p-8 rounded-lg shadow-md">
              <p className="text-gray-700 mb-6">
                Here's an example calculation using sample data:
              </p>
              
              <div className="bg-gray-50 p-6 rounded-lg">
                <h4 className="font-semibold mb-4">Sample Data:</h4>
                <ul className="list-disc pl-8 space-y-2">
                  <li>Pot Size: {exampleData.potSize} currency</li>
                  <li>Bet Size: {exampleData.betSize} currency</li>
                  <li>Win Probability: {exampleData.winProbability}%</li>
                </ul>
                
                <h4 className="font-semibold mt-6 mb-4">Calculations:</h4>
                <ol className="list-decimal pl-8 space-y-4">
                  <li>
                    <p>Expected Value Calculation:</p>
                    <ul className="list-none pl-8 space-y-2">
                      <li>Expected Value = (Win Probability / 100) * (Pot Size + Bet Size) - Bet Size = ({exampleData.winProbability} / 100) * ({exampleData.potSize} + {exampleData.betSize}) - {exampleData.betSize} = {exampleData.ev} currency</li>
                    </ul>
                  </li>
                </ol>
                
                <p className="mt-6 font-semibold">Interpretation:</p>
                <p>In this example, with a pot size of {exampleData.potSize} currency, a bet size of {exampleData.betSize} currency, and a win probability of {exampleData.winProbability}%, the expected value of the bet is {exampleData.ev} currency.</p>
              </div>
            </div>
          </div>

          <div className="mt-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Understanding Poker EV <span className="text-blue-600">📊</span></h2>
            <div className="text-gray-600 mb-8">
              <p>Poker EV (Expected Value) is a fundamental concept in poker strategy that helps players make profitable decisions. It calculates the average amount a player can expect to win or lose per bet over a large number of similar situations. This calculator helps you:</p>
              <ul className="list-disc pl-6 mt-2">
                <li>Calculate the expected value of your bet</li>
                <li>Determine whether a bet is profitable in the long run</li>
                <li>Make more informed decisions at the poker table</li>
              </ul>
              <p className="italic mt-2">Note: Poker EV assumes that the win probability is accurate. Use this tool as a guide and consider other factors that may affect your decision-making at the table.</p>
            </div>
          </div>

          <div className="mt-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">How to Use Poker EV Calculator <span className="text-blue-600">📋</span></h2>
            <div className="text-gray-600">
              <p>Follow these steps to use the Poker EV Calculator effectively:</p>
              
              <div className="mt-4">
                <h3 className="text-lg font-semibold text-blue-600 mb-2">Step 1: Enter Your Data</h3>
                <ul className="list-disc pl-6 mt-2">
                  <li>Enter the pot size</li>
                  <li>Enter the bet size</li>
                  <li>Enter the win probability</li>
                </ul>
              </div>

              <div className="mt-6">
                <h3 className="text-lg font-semibold text-blue-600 mb-2">Step 2: Calculate EV</h3>
                <ul className="list-disc pl-6 mt-2">
                  <li>Click "Calculate EV" to generate the results</li>
                  <li>View the expected value of your bet</li>
                </ul>
              </div>

              <div className="mt-6">
                <h3 className="text-lg font-semibold text-blue-600 mb-2">Step 3: Interpret Results</h3>
                <ul className="list-disc pl-6 mt-2">
                  <li>Use the expected value to determine if the bet is profitable</li>
                  <li>Consider the expected value in conjunction with other poker strategies</li>
                </ul>
              </div>

              <div className="mt-6 bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-blue-600 mb-2">Important Considerations</h3>
                <ul className="list-disc pl-6 mt-2">
                  <li>Ensure accurate and realistic win probability estimates</li>
                  <li>Consider other factors that may affect your decision-making, such as opponent behavior and game dynamics</li>
                  <li>Use Poker EV as a guide and combine it with other poker tools and techniques for better results</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PokerEVCalculator;